/**
 * Rounds a 0–5 motivation intensity to the single decimal the UI renders via
 * `score.toFixed(1)`. Using this everywhere a score is compared keeps derived
 * numbers (gaps, deltas) in lockstep with the displayed value.
 *
 * Note this intentionally mirrors `toFixed(1)` rather than `Math.round(x * 10) /
 * 10`: the two disagree for values ending in `.x5`. The nearest double to
 * `2.05` is `2.0499…`, which `toFixed` sees and rounds *down* to `2.0`, while
 * `2.05 * 10` rounds back up to exactly `20.5` so `Math.round` gives `2.1`.
 * Since the UI renders `score.toFixed(1)`, deriving a gap any other way lets a
 * card show `2.0 / 1.9` beside a gap that doesn't reconcile with those numbers.
 */
export const roundScoreToDisplay = (score: number): number => Number(score.toFixed(1))

/**
 * Signed difference between two scores computed from their displayed (1-decimal)
 * values, so any gap/delta derived from it always reconciles with the `2.0 / 1.9`
 * numbers shown beside it. Positive means `a` is the higher (leading) side.
 */
export const displayedScoreGap = (a: number, b: number): number => roundScoreToDisplay(a) - roundScoreToDisplay(b)

/**
 * Formats a percentage-point difference with an explicit sign, e.g. `+4.2%` /
 * `-4.2%`. The sign is prepended to the absolute value so `-0.04` renders as
 * `-0.0%` rather than the `-0.0%`/`0.0%` mix `toFixed` alone produces.
 */
export const formatPp = (value: number): string => `${value >= 0 ? '+' : '-'}${Math.abs(value).toFixed(1)}%`

/** Rendered in place of a value the API omitted (e.g. a delta with no previous month to compare with). */
export const NO_VALUE = '–'

/** {@link formatPp}, or {@link NO_VALUE} when the delta is absent. */
export const formatDelta = (deltaPp: number | undefined): string => (deltaPp === undefined ? NO_VALUE : formatPp(deltaPp))

/** A 0–100 share with one decimal, e.g. `12.5%`. */
export const formatSharePct = (sharePct: number): string => `${sharePct.toFixed(1)}%`

/** A 0–1 install share as a percentage, e.g. `0.031` → `3.1%`, or {@link NO_VALUE} when absent. */
export const formatInstallShare = (installShare: number | undefined): string => (installShare === undefined ? NO_VALUE : formatSharePct(installShare * 100))

/** A video length in whole seconds, e.g. `15s`. */
export const formatSeconds = (seconds: number): string => `${Math.round(seconds)}s`

/**
 * Format an Ad Performance value (0-100) for display. Strong performers drop
 * the decimal so the chip stays compact (`78%`); sub-10 values keep one decimal
 * so a weak-but-real signal doesn't round to `0%` and disappear (`3.4%`).
 */
export const formatPerf = (perf: number): string => (perf >= 10 ? perf.toFixed(0) : perf.toFixed(1))
